import Button from "components/Button/Button"
import InputDefault from "components/Input/InputDefault"
import InputPhone from "components/Input/InputPhone"
import { STATUS_INPUT } from "components/Input/types"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { useString, useBoolean } from "helpers/hooks"
import FormInputCard from "pages/auth/molecules/FormInputCard"
import {
  isValidPhone,
  convertUrlBase64,
  getBase64,
  isUploadedFileWithWorkspaceId,
} from "helpers/utils"
import { ReactComponent as IconUpdateAvatar } from "assets/images/icons/icon-update-avatar.svg"

import {
  getUserMiddleware,
  postAddOrganizationMiddleware,
} from "pages/auth/services/api"
import React, { useEffect, useState } from "react"
import {
  getOrganizationDetail,
  updateOrganization,
} from "./organization.services"
import { toast } from "react-toastify"
import { endLoading, startLoading } from "reducers/loading"
import { configureStore } from "stores/configureStore"
import PageLayout from "pages/layout/PageLayout"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { useLocation } from "react-router-dom"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import MultipleInput from "components/Input/MultipleInput"
import { IconPickerModal } from "./modal/IconPickerModal"
import { TippyCustomzie } from "components/TippyCustomzie"
import ActionItem from "pages/projects/molecules/ActionItem"
import { useAppSelector } from "hooks/useApp"
import { closeModal, openModal } from "reducers/modal"
import { getIconByName } from "components/OrganizationIconCard"

export const AddOrganizationPage = () => {
  const userInfo = useAppSelector((state) => state.userInfo)
  const location = useLocation()
  const workspaceId = userInfo?.last_organization_id
  const isEditMode = location.pathname === PATH.organizationEdit

  const name = useString("")
  const address = useString("")
  const postalCode = useString("")
  const description = useString("")
  const phoneNumber = useString("")
  const website = useString("")
  const [errorNameMessage, setErrorNameMessage] = useState<string>("")
  const [errorWebsiteMessage, setErrorWebsiteMessage] = useState<string>("")

  const [selectedIcon, setSelectedIcon] = useState<any>(null)
  const [uploadedFile, setUploadedFile] = useState<string | null>(null)
  const [isOpenIconPickerModal, setIsOpenIconPickerModal] =
    useState<boolean>(false)
  const openSelectIcon = useBoolean(false)
  const [selectedBgIcon, setSelectedBgIcon] = useState<string>("")
  const isLoadingUploadFile = useBoolean(false)
  const isLoadingOrganizationDetail = useBoolean(true)
  const orginLogo = useString("")

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!isEditMode && !userInfo?.is_added_organization) {
      pushTo(PATH.notWorkspace)
    }
  }, [userInfo?.id, isEditMode])

  useEffect(() => {
    if (!isEditMode || !workspaceId || !userInfo?.id) {
      name.setValue("")
      address.setValue("")
      postalCode.setValue("")
      description.setValue("")
      phoneNumber.setValue("")
      website.setValue("")
      setSelectedBgIcon("")
      setSelectedIcon(null)
      setUploadedFile(null)
      setErrorNameMessage("")
      setErrorWebsiteMessage("")
      isLoadingOrganizationDetail.setValue(false)
      return
    }
    isLoadingOrganizationDetail.setValue(true)
    const isOwner =
      userInfo.organizations?.find((el) => el.id === workspaceId)?.type ===
      "OWNER"
    if (!isOwner) {
      pushTo(PATH.workspaceSetting)
      return
    }
    getOrganizationDetail(workspaceId, true)
      .then((organizationDetail) => {
        name.setValue(organizationDetail.name || "")
        address.setValue(organizationDetail.profile?.address || "")
        postalCode.setValue(organizationDetail.profile?.postal_code || "")
        description.setValue(organizationDetail.profile?.description || "")
        phoneNumber.setValue(organizationDetail.profile?.phone_number || "")
        website.setValue(organizationDetail.profile?.website || "")
        setSelectedBgIcon(organizationDetail.profile?.background_color || "")
        const iconUrl = organizationDetail.profile?.logo_url
        orginLogo.setValue(iconUrl || "")
        if (iconUrl) {
          const isUploadedFile = isUploadedFileWithWorkspaceId(
            iconUrl,
            workspaceId
          )

          if (isUploadedFile) {
            setUploadedFile(iconUrl)
          } else {
            const newIcon = getIconByName(iconUrl)
            if (newIcon) {
              setSelectedIcon(newIcon)
            }
          }
        }
        isLoadingOrganizationDetail.setValue(false)
      })
      .catch((error) => {
        isLoadingOrganizationDetail.setValue(false)
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ??
              "Failed to load workspace details!"
            }
            type="error"
          />
        )
      })
  }, [isEditMode, workspaceId, userInfo?.id])

  const handleChangeInputType = (type: string) => (event: any) => {
    const value = event.target.value
    if (type === "name") {
      name.setValue(value)
      if (!value) {
        setErrorNameMessage("")
      } else {
        const validation = validateOrganizationName(value)
        setErrorNameMessage(validation.isValid ? "" : validation.error || "")
      }
      return
    }
    if (type === "website") {
      website.setValue(value)
      if (!value) {
        setErrorWebsiteMessage("")
      } else {
        setErrorWebsiteMessage(
          isValidUrl(value)
            ? ""
            : "Please enter a valid URL (e.g., https://example.com)"
        )
      }
      return
    }
    switch (type) {
      case "address":
        address.setValue(value)
        break
      case "postalCode":
        postalCode.setValue(value)
        break
      case "description":
        description.setValue(value)
        break
    }
  }

  const handleChangePhoneNumber = (value) => {
    phoneNumber.setValue(value)
  }

  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (handleDisableButton()) {
        return
      }
      onSave()
    }
  }

  const validateOrganizationName = (
    name: string
  ): { isValid: boolean; error?: string } => {
    // Check if name is empty or only whitespace
    if (!name || !name.trim()) {
      return {
        isValid: false,
        error: "Workspace name cannot be empty",
      }
    }

    // Check for special characters (only allow letters, numbers, and spaces)
    const allowedPattern = /^[a-zA-Z0-9\s]+$/
    if (!allowedPattern.test(name)) {
      return {
        isValid: false,
        error:
          "Workspace name contains invalid characters. Only letters, numbers, and spaces are allowed",
      }
    }

    return { isValid: true }
  }
  const isValidUrl = (string: string): boolean => {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  const handleChangeIcon = (id: string) => () => {
    openSelectIcon.setValue(false)
    if (id === "select-icon") {
      setIsOpenIconPickerModal(true)
      return
    }
    if (id === "upload-icon") {
      // Create a file input element and trigger it
      const fileInput = document.createElement("input")
      fileInput.type = "file"
      fileInput.accept = "image/*"
      fileInput.onchange = (event: any) => {
        const file = event.target.files[0]
        if (file) {
          const maxSize = 3 * 1024 * 1024
          if (file.size > maxSize) {
            toast(
              <LabelNotificationPage
                messenger="File size must be less than 3MB"
                type="error"
              />
            )
          } else {
            isLoadingUploadFile.setValue(true)
            getBase64(file)
              .then((data) => {
                setUploadedFile(String(data))
                setSelectedIcon(null)
                setSelectedBgIcon("")
                isLoadingUploadFile.setValue(false)
              })
              .catch(() => {
                isLoadingUploadFile.setValue(false)
                toast(
                  <LabelNotificationPage
                    messenger={MESSENGER_NOTIFICATION.IMAGE_INVALID}
                    type="error"
                  />
                )
              })
          }
        }
      }
      fileInput.click()
      return
    }
    setIsOpenIconPickerModal(true)
  }

  const onSelectedIcon = (icon: any, bgIcon: string) => {
    setSelectedIcon(icon)
    setSelectedBgIcon(bgIcon)
    setUploadedFile(null)
  }

  const handleDisableButton = () => {
    if (!name.value) {
      return true
    }
    const validation = validateOrganizationName(name.value)
    if (!validation.isValid) {
      return true
    }
    if (website.value && !isValidUrl(website.value)) {
      return true
    }
    if (phoneNumber.value && !isValidPhone(phoneNumber.value)) {
      return true
    }
    return false
  }

  const onSave = async () => {
    configureStore.dispatch(startLoading())
    try {
      if (isEditMode && workspaceId) {
        const payloadEdit: any = {
          name: name.value,
          address: address.value,
          postal_code: postalCode.value,
          description: description.value,
          phone_number: phoneNumber.value,
          website: website.value,
          bg_icon: selectedBgIcon,
        }
        if (uploadedFile && orginLogo.value !== uploadedFile) {
          payloadEdit.icon = convertUrlBase64(uploadedFile)
          payloadEdit.is_upload_icon = true
        }
        if (
          selectedIcon &&
          selectedIcon.name &&
          selectedIcon.name !== orginLogo.value
        ) {
          payloadEdit.icon = selectedIcon.name
          payloadEdit.is_upload_icon = false
        }
        await updateOrganization(workspaceId, payloadEdit)
        getUserMiddleware()
        configureStore.dispatch(endLoading())
        toast(
          <LabelNotificationPage
            messenger="Update workspace successfully!"
            type="success"
          />
        )
        pushTo(PATH.workspaceSetting)
        return
      }
      const payloadAdd = {
        name: name.value,
        address: address.value,
        postal_code: postalCode.value,
        description: description.value,
        phone_number: phoneNumber.value,
        website: website.value,
        icon: selectedIcon
          ? selectedIcon.name
          : uploadedFile
          ? convertUrlBase64(uploadedFile)
          : null,
        bg_icon: selectedBgIcon,
        is_upload_icon: uploadedFile ? true : false,
      }
      await postAddOrganizationMiddleware(payloadAdd)
      getUserMiddleware((typeStatus) => {
        if (typeStatus) {
          name.setValue("")
          address.setValue("")
          postalCode.setValue("")
          description.setValue("")
          phoneNumber.setValue("")
          website.setValue("")
          setSelectedIcon(null)
          setSelectedBgIcon("")
          setUploadedFile(null)
          setErrorNameMessage("")
          configureStore.dispatch(endLoading())
          configureStore.dispatch(
            openModal({
              type: "Delete",
              props: {
                deleteModal: {
                  title: `The setup might take a few minutes. You'll get an email when everything's ready.`,
                  label: `We're setting up your workspace`,
                  content: "",
                  onSubmit: async () => {
                    closeModal()
                    pushTo(PATH.projects)
                  },
                  styleTitle: { textAlign: "center" },
                  titleButton: "Go back to Projects",
                  colorYellowButton: true,
                  isButtonCancel: false,
                },
              },
            })
          )
        }
      })
    } catch (error: any) {
      configureStore.dispatch(endLoading())
      toast(
        <LabelNotificationPage
          messenger={
            error.response?.data?.message ??
            (isEditMode ? "Update workspace failed!" : "Add workspace failed!")
          }
          type="error"
        />
      )
    }
  }
  const onCancel = () => {
    pushTo(PATH.projects)
  }

  const renderBody = () => {
    if (isEditMode && isLoadingOrganizationDetail.value) {
      return (
        <div className="flex w-full p-[24px]">
          <div className="flex w-[70%] border border-[#E4E4E4] rounded-[6px] p-4 bg-[#FFFFFF] gap-[32px]">
            <div className="flex flex-col relative">
              <div className="h-[128px] w-[128px] bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700" />
            </div>
            <div className="flex-1 flex flex-col w-full">
              <div className="h-[64px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
              <div className="grid grid-cols-2 gap-[16px] w-full">
                <div className="h-[64px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
                <div className="h-[64px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
              </div>
              <div className="grid grid-cols-2 gap-[16px] w-full">
                <div className="h-[64px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
                <div className="h-[64px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
              </div>
              <div className="h-[110px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />

              <div className="w-full flex justify-end">
                <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between mt-[24px] w-[250px]">
                  <div className="h-[44px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
                  <div className="h-[44px] w-full bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700 mb-3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return (
      <div className="flex w-full p-[24px]">
        <div className="flex w-[70%] border border-[#E4E4E4] rounded-[6px] p-4 bg-[#FFFFFF] gap-[32px]">
          <div className="flex flex-col relative">
            <div
              className={`flex items-center justify-center`}
              style={{
                border: "1px solid #E4E4E4",
                width: 128,
                height: 128,
                borderRadius: 8,
                backgroundColor: uploadedFile
                  ? undefined
                  : selectedBgIcon
                  ? selectedBgIcon
                  : "#F7F7F7",
                backgroundImage: uploadedFile
                  ? `url(${uploadedFile})`
                  : undefined,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            >
              {selectedIcon && selectedIcon.component
                ? React.createElement(
                    selectedIcon.component as React.ComponentType<{
                      size?: number
                      color?: string
                    }>,
                    { size: 80, color: "#FFFFFF" }
                  )
                : null}
            </div>
            <TippyCustomzie
              containerClass="component-card-menu reset-tippy"
              placement="right-start"
              interactive
              arrow={false}
              animation="scale"
              visible={openSelectIcon.value}
              offset={[-12, 6]}
              onClickOutside={() => openSelectIcon.setValue(false)}
              content={
                <div
                  style={{
                    background: "#222222",
                    borderRadius: 4,
                    paddingTop: 8,
                    paddingBottom: 8,
                    width: 120,
                  }}
                >
                  {[
                    {
                      label: "Select Icon",
                      id: "select-icon",
                    },
                    {
                      label: "Upload Icon",
                      id: "upload-icon",
                    },
                  ].map((status, index) => (
                    <ActionItem
                      key={index}
                      title={status.label}
                      onClick={handleChangeIcon(status.id)}
                    />
                  ))}
                </div>
              }
              allowHTML
            >
              <div
                onClick={() => openSelectIcon.setValue(true)}
                className="absolute cursor-pointer"
                style={{
                  top: 110,
                  right: -12,
                }}
              >
                <IconUpdateAvatar />
              </div>
            </TippyCustomzie>

            {/*  */}
          </div>
          <div className="flex-1 flex flex-col w-full">
            <FormInputCard title="Name" required>
              <InputDefault
                value={name.value}
                onChange={handleChangeInputType("name")}
                onKeyPress={onKeyPress}
                status={
                  errorNameMessage ? STATUS_INPUT.ERROR : STATUS_INPUT.DEFAULT
                }
                labelStatus={errorNameMessage}
              />
            </FormInputCard>
            <div className="grid grid-cols-2 gap-[16px] w-full">
              <FormInputCard title="Address">
                <InputDefault
                  value={address.value}
                  onChange={handleChangeInputType("address")}
                />
              </FormInputCard>

              <FormInputCard title="Postal Code">
                <InputDefault
                  value={postalCode.value}
                  onChange={handleChangeInputType("postalCode")}
                />
              </FormInputCard>
            </div>
            <div className="grid grid-cols-2 gap-[16px] w-full">
              <FormInputCard title="Contact">
                <InputPhone
                  value={phoneNumber.value}
                  setValue={handleChangePhoneNumber}
                  onEnterKeyPress={onKeyPress}
                />
                {phoneNumber.value ? (
                  <div className="flex mt-1">
                    <p
                      className="font-semibold"
                      style={{
                        color: isValidPhone(phoneNumber.value)
                          ? "#56A012"
                          : "#EA4545",
                        fontSize: 10,
                        lineHeight: "14px",
                      }}
                    >
                      {isValidPhone(phoneNumber.value)
                        ? "Valid phone number format!"
                        : "Invalid phone number format!"}
                    </p>
                  </div>
                ) : null}
              </FormInputCard>

              <FormInputCard title="Website">
                <InputDefault
                  value={website.value}
                  onChange={handleChangeInputType("website")}
                  status={
                    errorWebsiteMessage
                      ? STATUS_INPUT.ERROR
                      : STATUS_INPUT.DEFAULT
                  }
                  labelStatus={errorWebsiteMessage}
                />
                {website.value ? (
                  <div className="flex mt-1">
                    <p
                      className="font-semibold"
                      style={{
                        color: isValidUrl(website.value)
                          ? "#56A012"
                          : "#EA4545",
                        fontSize: 10,
                        lineHeight: "14px",
                      }}
                    >
                      {isValidUrl(website.value)
                        ? "Valid URL format!"
                        : "Invalid URL format!"}
                    </p>
                  </div>
                ) : null}
              </FormInputCard>
            </div>
            <FormInputCard title="Description">
              <MultipleInput
                value={description.value}
                onChange={handleChangeInputType("description")}
                rows={3}
                maxLength={256}
              />
            </FormInputCard>
            <div className="w-full flex justify-end">
              <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between mt-[24px] w-[250px]">
                <Button
                  widthBtn={"100%"}
                  title="Cancel"
                  colorBtn="white"
                  onClick={onCancel}
                />
                <Button
                  widthBtn="100%"
                  title={isEditMode ? "Update" : "Save"}
                  colorBtn="yellow"
                  onClick={onSave}
                  disabled={
                    handleDisableButton() || isLoadingOrganizationDetail.value
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <PageLayout
      childrenHeader={
        <div className="flex items-center justify-between h-full px-[24px]">
          {isEditMode && isLoadingOrganizationDetail.value ? (
            <div className="h-[24px] w-[120px] bg-[#EFEFEF] rounded-[4px] dark:bg-gray-700" />
          ) : (
            <h2 className="text-[16px] font-semibold lh-26 text-black">
              {isEditMode ? "Edit workspace" : "Add workspace"}
            </h2>
          )}
        </div>
      }
    >
      {renderBody()}
      {isOpenIconPickerModal ? (
        <IconPickerModal
          currentIcon={selectedIcon}
          currentBgIcon={selectedBgIcon}
          isOpen={isOpenIconPickerModal}
          onSelectedIcon={onSelectedIcon}
          onClose={() => setIsOpenIconPickerModal(false)}
        />
      ) : null}
    </PageLayout>
  )
}

export default AddOrganizationPage
